/**
 * DADDL featured tabs — centered pack carousel (same math as video trust).
 * Viewport clips the track; neighbours peek at both edges.
 * Neighbour pack links centre that slide; only the focused pack opens the PDP.
 * Title / stars / price / View product render once in a static stage under
 * the track (bindStage), so only the packs travel; the stage crossfades when
 * the active product changes. Steer arrows are static in the stage; panel
 * click delegates go().
 * Grab / swipe is daddlBindCenteredLoop (daddl-carousel.js). No Swiper.
 */
(function () {
  'use strict';

  /* Clones are aria-hidden: every link/button/tab stop inside leaves the Tab order. */
  function stripCloneControls(slide) {
    slide.querySelectorAll('a[href], button, [tabindex]').forEach(function (el) {
      el.setAttribute('tabindex', '-1');
    });
    slide.querySelectorAll('form').forEach(function (form) {
      form.setAttribute('aria-hidden', 'true');
      form.querySelectorAll('button, input, select, textarea').forEach(function (ctrl) {
        ctrl.disabled = true;
      });
    });
    slide.querySelectorAll('[data-daddl-ft-prev], [data-daddl-ft-next]').forEach(function (btn) {
      btn.disabled = true;
    });
  }

  /*
   * Static info stage. Copies the active card's .daddl-fc__info into three
   * slots: title, meta (stars, coming-soon, price) and CTA. Loop clones carry
   * tabindex="-1" on their links, so copies drop it. Rebases (clone → real
   * slide of the same product) do not re-render.
   */
  function bindStage(panel) {
    var stage = panel.querySelector('[data-daddl-ft-stage]');
    if (!stage) return null;
    var slots = {
      title: stage.querySelector('[data-daddl-ft-stage-title]'),
      meta: stage.querySelector('[data-daddl-ft-stage-meta]'),
      cta: stage.querySelector('[data-daddl-ft-stage-cta]'),
    };
    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
    var currentKey = null;
    var swapTimer = 0;

    function copy(node) {
      var clone = node.cloneNode(true);
      [clone].concat(Array.prototype.slice.call(clone.querySelectorAll('[tabindex="-1"]'))).forEach(function (el) {
        if (el.getAttribute && el.getAttribute('tabindex') === '-1') el.removeAttribute('tabindex');
      });
      return clone;
    }

    function fill(info) {
      slots.title.replaceChildren();
      slots.meta.replaceChildren();
      slots.cta.replaceChildren();
      Array.prototype.forEach.call(info.children, function (child) {
        if (child.classList.contains('daddl-fc__title')) slots.title.appendChild(copy(child));
        else if (child.classList.contains('daddl-fc__view')) slots.cta.appendChild(copy(child));
        else slots.meta.appendChild(copy(child));
      });
    }

    stage.hidden = false;
    panel.classList.add('is-staged');

    return function show(slide, instant) {
      var info = slide && slide.querySelector('.daddl-fc__info');
      if (!info) return;
      var link = info.querySelector('.daddl-fc__title a');
      var key = link ? link.getAttribute('href') : info.textContent;
      if (key === currentKey) return;
      var first = currentKey === null;
      currentKey = key;
      window.clearTimeout(swapTimer);

      if (first || instant || reduce.matches) {
        fill(info);
        stage.classList.remove('is-swapping');
        return;
      }
      stage.classList.add('is-swapping');
      swapTimer = window.setTimeout(function () {
        fill(info);
        stage.classList.remove('is-swapping');
      }, 160);
    };
  }

  function bindPanel(panel) {
    if (panel.daddlFtBound) return;
    if (typeof window.daddlBindCenteredLoop !== 'function') {
      document.addEventListener('DOMContentLoaded', function () { bindPanel(panel); });
      return;
    }

    var viewport = panel.querySelector('[data-daddl-ft-viewport]');
    var track = panel.querySelector('[data-daddl-ft-track]');
    if (!viewport || !track) return;

    panel.daddlFtBound = true;
    var showStage = bindStage(panel);

    var loopCtl = window.daddlBindCenteredLoop({
      viewport: viewport,
      track: track,
      slideSelector: '[data-daddl-ft-slide]',
      cloneAttr: 'data-daddl-ft-clone',
      instantClass: 'is-instant',
      ignoreDown: '[data-daddl-ft-prev], [data-daddl-ft-next], [data-tilt-flip-btn]',
      prepareClone: stripCloneControls,
      onActive: function (index, slides, meta) {
        slides.forEach(function (el, i) {
          var on = i === index;
          el.classList.toggle('is-active', on);
          var tilt = el.querySelector('daddl-pack-tilt');
          if (tilt && typeof tilt.setCarouselActive === 'function') {
            tilt.setCarouselActive(on);
          }
        });
        if (showStage) showStage(slides[index], meta && meta.instant && !meta.rebased);
      }
    });

    if (!loopCtl && showStage) showStage(track.querySelector('[data-daddl-ft-slide]'), true);

    /* Neighbour packs are links — a tap should centre them, not open the PDP.
       Steer arrows live on every card; only the active row is clickable. */
    if (loopCtl) {
      panel.addEventListener('click', function (event) {
        if (event.target.closest('[data-daddl-ft-prev]')) {
          event.preventDefault();
          loopCtl.go(-1);
          return;
        }
        if (event.target.closest('[data-daddl-ft-next]')) {
          event.preventDefault();
          loopCtl.go(1);
        }
      });

      track.addEventListener('click', function (event) {
        var slide = event.target.closest('[data-daddl-ft-slide]');
        if (!slide || slide.classList.contains('is-active')) return;
        if (event.target.closest('[data-tilt-flip-btn]')) return;
        event.preventDefault();
        event.stopPropagation();
        var index = loopCtl.slides.indexOf(slide);
        if (index >= 0) loopCtl.goTo(index, true);
      }, true);
    }

    panel.daddlFtPosition = function (animate) {
      if (loopCtl) loopCtl.position(!!animate);
    };
  }

  class DaddlFeaturedTabs extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.tabs = this.querySelectorAll('[data-daddl-ft-tab]');
      this.panels = this.querySelectorAll('[data-daddl-ft-panel]');

      this.tabs.forEach((tab) => {
        tab.addEventListener('click', () => this.activate(tab));
      });

      this.panels.forEach((panel) => {
        if (!panel.hidden) bindPanel(panel);
      });
    }

    activate(tab) {
      const id = tab.getAttribute('data-daddl-ft-tab');
      this.tabs.forEach((item) => {
        const on = item === tab;
        item.classList.toggle('is-active', on);
        item.setAttribute('aria-selected', on ? 'true' : 'false');
      });
      this.panels.forEach((panel) => {
        const show = panel.getAttribute('data-daddl-ft-panel') === id;
        panel.hidden = !show;
        if (show) {
          bindPanel(panel);
          if (typeof panel.daddlFtPosition === 'function') panel.daddlFtPosition(false);
        }
      });
    }
  }

  if (!customElements.get('daddl-featured-tabs')) {
    customElements.define('daddl-featured-tabs', DaddlFeaturedTabs);
  }
})();
